import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';

import toast from 'components/partials/toast/toast';

import { useApiRenameConversation } from './use-api-rename-conversation';

interface UseSessionRenameArgs {
  conversationId: string;
  conversationName: string;
  oktaUserId: string;
  clientId: string | number;
}

interface UseSessionRenameResult {
  isRenaming: boolean;
  draftName: string;
  displayName: string;
  isSaving: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  handleStartRename: () => void;
  handleDraftNameChange: (value: string) => void;
  handleSaveRename: () => Promise<void>;
  handleRenameKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const useSessionRename = ({
  conversationId,
  conversationName,
  oktaUserId,
  clientId,
}: UseSessionRenameArgs): UseSessionRenameResult => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(conversationName);
  const [pendingDisplayName, setPendingDisplayName] = useState<string | null>(
    null
  );

  const { mutateAsync: renameConversation, isPending: isSaving } =
    useApiRenameConversation({ oktaUserId, clientId });

  const displayName = pendingDisplayName ?? conversationName;

  useEffect(() => {
    if (!isRenaming) {
      setDraftName(conversationName);
    }
  }, [conversationName, isRenaming]);

  useEffect(() => {
    if (
      pendingDisplayName !== null &&
      conversationName.trim() === pendingDisplayName
    ) {
      setPendingDisplayName(null);
    }
  }, [pendingDisplayName, conversationName]);

  useEffect(() => {
    if (!isRenaming) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isRenaming]);

  const handleStartRename = useCallback((): void => {
    setPendingDisplayName(null);
    setDraftName(conversationName);
    setIsRenaming(true);
  }, [conversationName]);

  const handleDraftNameChange = useCallback((value: string): void => {
    setDraftName(value);
  }, []);

  const handleCancelRename = useCallback((): void => {
    if (isSaving) {
      return;
    }

    setDraftName(conversationName);
    setIsRenaming(false);
  }, [conversationName, isSaving]);

  const handleSaveRename = useCallback(async (): Promise<void> => {
    if (isSaving) {
      return;
    }

    const trimmedName = draftName.trim();

    if (!trimmedName) {
      toast.error({
        title: 'Chat name is required',
        message: 'Please enter a name before saving.',
      });

      return;
    }

    if (trimmedName === conversationName.trim()) {
      setIsRenaming(false);

      return;
    }

    try {
      await renameConversation({ conversationId, name: trimmedName });
      setPendingDisplayName(trimmedName);
      setIsRenaming(false);
    } catch {
      // Error toast is handled in the mutation hook.
    }
  }, [
    conversationId,
    conversationName,
    draftName,
    isSaving,
    renameConversation,
  ]);

  const handleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancelRename();

        return;
      }

      if (event.key !== 'Enter' || isSaving) {
        return;
      }

      event.preventDefault();
      void handleSaveRename();
    },
    [handleCancelRename, handleSaveRename, isSaving]
  );

  return {
    isRenaming,
    draftName,
    displayName,
    isSaving,
    inputRef,
    handleStartRename,
    handleDraftNameChange,
    handleSaveRename,
    handleRenameKeyDown,
  };
};
